import { useMutation } from "@apollo/react-hooks";
import React from "react";
import { useForm } from "react-hook-form";
import styled, { css } from "styled-components";

import { PanelContext } from "../../contexts/PanelContext";
import {
  Create_User_SubmissionMutation,
  Create_User_SubmissionMutationVariables,
  SayingInput,
} from "../../generated/graphql";
import { CREATE_USER_SUBMISSION } from "../../graphql/mutations/create-user-submission";
import FormError from "../form/FormError";
import FormRow from "../form/FormRow";
import Label from "../form/Label";
import SubmitWrapper from "../form/SubmitWrapper";
import TextInput from "../form/TextInput";
import { Button } from "../shared/Button";
import CircleIcon from "../shared/CircleIcon";
import Text from "../shared/Text";
import { InnerWrapper } from "./InnerWrapper";
import SuccessMessage from "./SuccessMessage";
import Reply from "./Reply";

const SubmitSayingContent: React.FC = () => {
  const [formPage, setFormPage] = React.useState(0);
  const [success, setSuccess] = React.useState<boolean>(false);
  const [{ data }, setPanel] = React.useContext(PanelContext);

  const {
    register,
    handleSubmit,
    errors,
    getValues,
    triggerValidation,
    clearError,
  } = useForm<SayingInput>();

  const [createUserSubmission, userSubmission] = useMutation<
    Create_User_SubmissionMutation,
    Create_User_SubmissionMutationVariables
  >(CREATE_USER_SUBMISSION);

  const onSubmit = handleSubmit(() => {
    const input = getValues();
    const parentId = data?.replyingTo?.id;
    if (parentId) {
      input.parent_nid = parentId;
    }
    // data.images = data.images.split(",");
    createUserSubmission({ variables: { input } });
  });

  React.useEffect(() => {
    if (userSubmission.data) {
      if (userSubmission.data.CreateSaying.errors?.length) {
        console.error(userSubmission.data.CreateSaying.errors);
      } else {
        setSuccess(true);
      }
    }
    if (userSubmission.error) {
      console.error(userSubmission.error);
    }
  }, [userSubmission]);
  return (
    <Container>
      <PanelHeader>
        <InnerWrapper>
          <HeaderTitle>
            <CircleIcon icon={"story"} color={"darkBlue"} />
            <Title>
              <Text as="h1">Submit your saying</Text>
            </Title>
          </HeaderTitle>
        </InnerWrapper>
      </PanelHeader>
      <InnerWrapper>
        {success ? (
          <SuccessMessage />
        ) : (
          <form onSubmit={onSubmit}>
            {data?.replyingTo && (
              <ReplyToWrapper
                onClick={() =>
                  setPanel({
                    open: true,
                    data: { type: "poi", id: data?.replyingTo?.id },
                  })
                }
              >
                <Text
                  as="h4"
                  color="midGrey"
                  style={{ paddingBottom: "0.5rem" }}
                >
                  Replying to
                </Text>
                <Reply
                  title={data.replyingTo.title}
                  region={data.replyingTo.region}
                  meaning={data.replyingTo.meaning}
                />
              </ReplyToWrapper>
            )}
            <FormTab className={formPage === 0 ? "active" : ""}>
              <FormRow>
                <Label>Your saying</Label>
                <TextInput name="saying" ref={register({ required: true })} />
                {errors.saying && (
                  <FormError>This field is required.</FormError>
                )}
              </FormRow>
              <FormRow>
                <Label>Meaning</Label>
                <TextInput
                  placeholder="What does your saying mean?"
                  name="meaning"
                  ref={register()}
                />
              </FormRow>
              <FormRow>
                <Label>Context</Label>
                <FormRow direction="row">
                  <TextInput
                    name="context"
                    placeholder="Use it in a sentence"
                    ref={register()}
                  />
                </FormRow>
              </FormRow>
              <FormRow>
                <Label>Town, city or region</Label>
                <TextInput
                  name="region"
                  placeholder="e.g. Manchester, South East, Devon"
                  ref={register({ required: true })}
                />
                {errors.region && (
                  <FormError>This field is required.</FormError>
                )}
              </FormRow>
              {/* <FormRow>
                <Label>Images & Illustrations</Label>
                <ImageUpload setImages={paths => setValue("images", paths)} />
                <input ref={register} name="images" hidden={true} />
              </FormRow> */}

              <SubmitWrapper>
                <Button
                  type="button"
                  onClick={() => {
                    triggerValidation().then(() => {
                      if (!errors.region && !errors.saying) {
                        clearError();
                        setFormPage(1);
                      }
                    });
                  }}
                >
                  Next
                </Button>
              </SubmitWrapper>
            </FormTab>
            <FormTab className={formPage === 1 ? "active" : ""}>
              <FormRow>
                <Label>Tell us your name</Label>
                <TextInput
                  name="submitted_by"
                  ref={register({ required: true })}
                />
                {errors.submitted_by && (
                  <FormError>This field is required.</FormError>
                )}
              </FormRow>
              <FormRow>
                <Label>Email</Label>
                <TextInput
                  name="email"
                  type="email"
                  ref={register({ required: true })}
                />
                {errors.email && <FormError>This field is required.</FormError>}
              </FormRow>
              <SubmitWrapper>
                <Button type="button" onClick={() => setFormPage(0)}>
                  Back
                </Button>

                <Button color="green" type="submit">
                  Submit saying
                </Button>
              </SubmitWrapper>
            </FormTab>
          </form>
        )}
      </InnerWrapper>
    </Container>
  );
};

const ReplyToWrapper = styled.div`
  padding: 1.8rem 2rem;
  cursor: pointer;
  margin-bottom: 3rem;
  display: flex;
  flex-direction: column;
  ${({ theme }) => css`
    background: ${theme.colors.bgAccent};
  `}
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  margin: 1rem 0;
  flex-direction: row;
  width: 100%;
`;

const FormTab = styled.div`
  &.active {
    display: block;
  }
  display: none;
`;
const Title = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 1rem;

  h1 {
    margin: 0;
    padding: 0.4rem 0;
  }
  h3 {
    padding: 0;
    margin: 0;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
  padding-bottom: 2.5rem;
  @media (max-width: 960px) {
    padding-bottom: 6rem;
  }
`;

const PanelHeader = styled.div`
  padding-bottom: 2rem;
`;

export default SubmitSayingContent;
